import React, { useState, useEffect, useContext } from 'react';
import { paymentsAPI } from '../../api/payments';
import { customersAPI } from '../../api/customers';
import { ordersAPI } from '../../api/orders';
import Button from '../common/Button';
import Modal from '../common/Modal';
import { formatCurrency, formatDateTime, formatPhone } from '../../utils/formatters';
import { NotificationContext } from '../../context/NotificationContext';
import './PaymentSystem.css';

const STATUS_META = {
  paid: { label: 'Paid', bg: '#d1fae5', color: '#065f46', icon: '✓' },
  pending: { label: 'Pending', bg: '#fef3c7', color: '#92400e', icon: '⏳' },
  failed: { label: 'Failed', bg: '#fee2e2', color: '#991b1b', icon: '✕' },
  refunded: { label: 'Refunded', bg: '#e0e7ff', color: '#3730a3', icon: '↩' },
};

const METHOD_META = {
  cash: { label: 'Cash', color: '#10b981', icon: '💵' },
  card: { label: 'Card', color: '#3b82f6', icon: '💳' },
  wallet: { label: 'Wallet', color: '#8b5cf6', icon: '👛' },
  on_credit: { label: 'Credit', color: '#f59e0b', icon: '📝' },
  subscription_credit: { label: 'Subscription', color: '#06b6d4', icon: '⭐' },
};

const PaymentSystem = () => {
  const { showNotification } = useContext(NotificationContext);
  
  const [activeTab, setActiveTab] = useState('all');
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [tabCounts, setTabCounts] = useState({});
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [methodFilter, setMethodFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Modals
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [showSettleModal, setShowSettleModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  
  // Wallet & Credit
  const [walletSummary, setWalletSummary] = useState(null);
  const [creditSummary, setCreditSummary] = useState(null);
  const [outstandingCredits, setOutstandingCredits] = useState([]);
  
  // Settlement
  const [settleCustomer, setSettleCustomer] = useState(null);
  const [settleOrders, setSettleOrders] = useState([]);
  const [selectedOrders, setSelectedOrders] = useState([]);
  
  // Refund
  const [refundReason, setRefundReason] = useState('');
  const [refundLoading, setRefundLoading] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  useEffect(() => {
    fetchPayments();
  }, [activeTab, searchTerm, statusFilter, methodFilter, startDate, endDate, currentPage]);

  const fetchDashboardData = async () => {
    try {
      const data = await paymentsAPI.getDashboardSummary();
      setSummary(data);
      setTabCounts({
        all: data.totalCount || 0,
        paid: data.paidCount || 0,
        pending: data.pendingCount || 0,
        failed: data.failedCount || 0,
        refunded: data.refundedCount || 0,
      });
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      showNotification?.('Failed to load dashboard', 'error');
    }
  };

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: 20,
        search: searchTerm || undefined,
        status: statusFilter || undefined,
        method: methodFilter || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      };

      if (activeTab !== 'all' && activeTab !== 'wallet' && activeTab !== 'credit') {
        params.status = activeTab;
      }

      const data = await paymentsAPI.getAll(params);
      setPayments(data.data || []);
      setTotalPages(data.pagination?.pages || 1);
    } catch (error) {
      console.error('Error fetching payments:', error);
      setPayments([]);
      showNotification?.('Failed to load payments', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchWalletData = async () => {
    try {
      const [wallet, credit, outstanding] = await Promise.all([
        paymentsAPI.getWalletSummary().catch(() => ({ data: { totalBalance: 0, activeWallets: 0, avgBalance: 0, customers: [] } })),
        paymentsAPI.getCreditSummary().catch(() => ({ data: { totalOutstanding: 0, totalLimit: 0, activeCredits: 0 } })),
        paymentsAPI.getOutstandingCredits().catch(() => ({ data: [] }))
      ]);
      
      setWalletSummary(wallet.data || wallet);
      setCreditSummary(credit.data || credit);
      setOutstandingCredits(outstanding.data || outstanding);
    } catch (error) {
      console.error('Error fetching wallet data:', error);
      showNotification?.('Failed to load wallet/credit data', 'error');
    }
  };

  const handleDeposit = async (formData) => {
    try {
      await paymentsAPI.depositWallet(formData);
      setShowDepositModal(false);
      showNotification?.('Deposit successful', 'success');
      fetchDashboardData();
      fetchPayments();
      if (activeTab === 'wallet') fetchWalletData();
    } catch (error) {
      console.error('Error depositing to wallet:', error);
      showNotification?.('Failed to deposit. Please try again.', 'error');
    }
  };

  const handleCreditUpdate = async (formData) => {
    try {
      await paymentsAPI.updateCreditLimit(formData);
      setShowCreditModal(false);
      showNotification?.('Credit limit updated', 'success');
      fetchDashboardData();
      if (activeTab === 'credit') fetchWalletData();
    } catch (error) {
      console.error('Error updating credit:', error);
      showNotification?.('Failed to update credit limit. Please try again.', 'error');
    }
  };

  const handleSettle = async (settleData) => {
    if (selectedOrders.length === 0) return;
    try {
      await paymentsAPI.settleOrders({
        customerId: settleCustomer._id,
        orderIds: selectedOrders,
        ...settleData,
      });
      setShowSettleModal(false);
      setSettleCustomer(null);
      setSelectedOrders([]);
      showNotification?.('Orders settled successfully', 'success');
      fetchDashboardData();
      fetchPayments();
      if (activeTab === 'credit') fetchWalletData();
    } catch (error) {
      console.error('Error settling orders:', error);
      showNotification?.('Failed to settle orders. Please try again.', 'error');
    }
  };

  const handleRefund = async () => {
    if (!refundReason.trim()) {
      showNotification?.('Please provide a refund reason', 'error');
      return;
    }
    setRefundLoading(true);
    try {
      await paymentsAPI.refund(selectedPayment._id, { reason: refundReason });
      showNotification?.('Payment refunded successfully', 'success');
      setShowRefundModal(false);
      setRefundReason('');
      setShowDetailModal(false);
      setSelectedPayment(null);
      fetchDashboardData();
      fetchPayments();
    } catch (error) {
      console.error('Error refunding payment:', error);
      showNotification?.(error.response?.data?.message || 'Failed to refund payment', 'error');
    } finally {
      setRefundLoading(false);
    }
  };

  const handlePrintReceipt = async (paymentId) => {
    try {
      const receipt = await paymentsAPI.getReceipt(paymentId);
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(receipt);
        printWindow.document.close();
        setTimeout(() => printWindow.print(), 100);
      }
    } catch (error) {
      console.error('Error printing receipt:', error);
      showNotification?.('Failed to print receipt. Please try again.', 'error');
    }
  };

  const handleGenerateInvoice = async (payment) => {
    try {
      const invoice = await paymentsAPI.generateInvoice(payment._id);
      const invoiceWindow = window.open('', '_blank');
      if (invoiceWindow) {
        invoiceWindow.document.write(invoice);
        invoiceWindow.document.close();
      }
    } catch (error) {
      console.error('Error generating invoice:', error);
      showNotification?.('Failed to generate invoice. Please try again.', 'error');
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setMethodFilter('');
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
  };

  const openSettleModal = async (customer) => {
    setSettleCustomer(customer);
    try {
      const data = await ordersAPI.getAll({ 
        customerId: customer._id, 
        paymentStatus: 'on_credit' 
      });
      setSettleOrders(data.data || []);
      setSelectedOrders([]);
      setShowSettleModal(true);
    } catch (error) {
      console.error('Error fetching orders:', error);
      showNotification?.('Failed to load orders. Please try again.', 'error');
    }
  };

  const hasActiveFilters = searchTerm || statusFilter || methodFilter || startDate || endDate;

  return (
    <div className="ps-root">
      {/* Header */}
      <div className="ps-header">
        <div>
          <h1 className="ps-title">💰 Payment Management</h1>
          <p className="ps-subtitle">Track and manage all payment transactions</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <Button onClick={() => setShowDepositModal(true)}>
            💵 Wallet Deposit
          </Button>
          <Button onClick={() => setShowCreditModal(true)} variant="secondary">
            📝 Manage Credit
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="ps-summary-row">
          <div className="ps-summary-card" style={{ borderTopColor: '#3b82f6' }}>
            <span className="ps-summary-icon">💰</span>
            <div>
              <div className="ps-summary-value">{formatCurrency(summary.totalAmount || 0)}</div>
              <div className="ps-summary-label">Total Payments</div>
              <div className="ps-summary-sub">{summary.totalCount || 0} transactions</div>
            </div>
          </div>
          <div className="ps-summary-card" style={{ borderTopColor: '#10b981' }}>
            <span className="ps-summary-icon">✓</span>
            <div>
              <div className="ps-summary-value">{formatCurrency(summary.paidAmount || 0)}</div>
              <div className="ps-summary-label">Paid</div>
              <div className="ps-summary-sub">{summary.paidCount || 0} payments</div>
            </div>
          </div>
          <div className="ps-summary-card" style={{ borderTopColor: '#f59e0b' }}>
            <span className="ps-summary-icon">⏳</span>
            <div>
              <div className="ps-summary-value">{formatCurrency(summary.pendingAmount || 0)}</div>
              <div className="ps-summary-label">Pending</div>
              <div className="ps-summary-sub">{summary.pendingCount || 0} pending</div>
            </div>
          </div>
          <div className="ps-summary-card" style={{ borderTopColor: '#8b5cf6' }}>
            <span className="ps-summary-icon">👛</span>
            <div>
              <div className="ps-summary-value">{formatCurrency(summary.walletAmount || 0)}</div>
              <div className="ps-summary-label">Wallet Balance</div>
              <div className="ps-summary-sub">{summary.walletsActive || 0} active</div>
            </div>
          </div>
          <div className="ps-summary-card" style={{ borderTopColor: '#ef4444' }}>
            <span className="ps-summary-icon">📝</span>
            <div>
              <div className="ps-summary-value">{formatCurrency(summary.creditOutstanding || 0)}</div>
              <div className="ps-summary-label">Credit Outstanding</div>
              <div className="ps-summary-sub">{summary.creditsActive || 0} customers</div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="ps-tabs">
        {[
          { key: 'all', label: 'All Payments', icon: '📊' },
          { key: 'paid', label: 'Paid', icon: '✓' },
          { key: 'pending', label: 'Pending', icon: '⏳' },
          { key: 'failed', label: 'Failed', icon: '✕' },
          { key: 'refunded', label: 'Refunded', icon: '↩' },
          { key: 'wallet', label: 'Wallet', icon: '👛' },
          { key: 'credit', label: 'Credit', icon: '📝' },
        ].map(tab => (
          <button
            key={tab.key}
            className={`ps-tab ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => { setActiveTab(tab.key); setCurrentPage(1); }}
          >
            <span className="ps-tab-icon">{tab.icon}</span>
            {tab.label}
            {tabCounts[tab.key] !== undefined && (
              <span className="om-tab-count">{tabCounts[tab.key]}</span>
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="ps-content">
        <div className="ps-tab-pane">
          {activeTab === 'wallet' ? (
            <WalletTab
              walletSummary={walletSummary}
              onLoad={fetchWalletData}
              onDeposit={() => setShowDepositModal(true)}
            />
          ) : activeTab === 'credit' ? (
            <CreditTab
              creditSummary={creditSummary}
              outstandingCredits={outstandingCredits}
              onLoad={fetchWalletData}
              onSettle={openSettleModal}
              onManageCredit={() => setShowCreditModal(true)}
            />
          ) : (
            <>
              {/* Filters */}
              <div className="ps-filters">
                <div className="ps-search-wrap">
                  <span className="ps-search-icon">🔍</span>
                  <input
                    type="text"
                    className="ps-search-input"
                    placeholder="Search by customer name, phone, or transaction ID..."
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                  />
                  {searchTerm && (
                    <button className="om-search-clear" onClick={() => setSearchTerm('')}>✕</button>
                  )}
                </div>
                <select
                  className="ps-filter-select"
                  value={statusFilter}
                  onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                >
                  <option value="">All Statuses</option>
                  <option value="paid">Paid</option>
                  <option value="pending">Pending</option>
                  <option value="failed">Failed</option>
                  <option value="refunded">Refunded</option>
                </select>
                <select
                  className="ps-filter-select"
                  value={methodFilter}
                  onChange={(e) => { setMethodFilter(e.target.value); setCurrentPage(1); }}
                >
                  <option value="">All Methods</option>
                  <option value="cash">Cash</option>
                  <option value="card">Card</option>
                  <option value="wallet">Wallet</option>
                  <option value="on_credit">Credit</option>
                  <option value="subscription_credit">Subscription</option>
                </select>
                <input
                  type="date"
                  className="ps-filter-date"
                  value={startDate}
                  onChange={(e) => { setStartDate(e.target.value); setCurrentPage(1); }}
                />
                <input
                  type="date"
                  className="ps-filter-date"
                  value={endDate}
                  onChange={(e) => { setEndDate(e.target.value); setCurrentPage(1); }}
                />
                {hasActiveFilters && (
                  <button className="om-clear-filters" onClick={clearFilters}>
                    ✕ Clear Filters
                  </button>
                )}
              </div>

              {/* Table Meta */}
              <div className="ps-table-meta">
                <span className="ps-count">
                  Showing {payments.length} of {tabCounts[activeTab] || 0} payments
                </span>
                {summary && (
                  <span className="ps-total-label">
                    Total Amount: <strong>{formatCurrency(summary.totalAmount || 0)}</strong>
                  </span>
                )}
              </div>

              {/* Table */}
              {loading ? (
                <div className="ps-loading">⏳ Loading payments...</div>
              ) : (
                <PaymentTable
                  payments={payments}
                  onPaymentClick={(payment) => {
                    setSelectedPayment(payment);
                    setShowDetailModal(true);
                  }}
                  onPrintReceipt={handlePrintReceipt}
                  onGenerateInvoice={handleGenerateInvoice}
                />
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="ps-pagination">
                  <Button
                    variant="secondary"
                    size="small"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    ← Previous
                  </Button>
                  <span className="ps-page-label">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="secondary"
                    size="small"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next →
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedPayment && (
        <Modal
          isOpen={showDetailModal}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedPayment(null);
          }}
          title={`Payment Details - ${selectedPayment.transactionId}`}
          size="large"
        >
          <PaymentDetailContent
            payment={selectedPayment}
            onPrintReceipt={handlePrintReceipt}
            onGenerateInvoice={handleGenerateInvoice}
            onRefund={() => {
              setShowDetailModal(false);
              setShowRefundModal(true);
            }}
          />
        </Modal>
      )}

      {/* Deposit Modal */}
      {showDepositModal && (
        <Modal
          isOpen={showDepositModal}
          onClose={() => setShowDepositModal(false)}
          title="💵 Wallet Deposit"
        >
          <WalletDepositForm
            onSubmit={handleDeposit}
            onCancel={() => setShowDepositModal(false)}
          />
        </Modal>
      )}

      {/* Credit Modal */}
      {showCreditModal && (
        <Modal
          isOpen={showCreditModal}
          onClose={() => setShowCreditModal(false)}
          title="📝 Manage Credit Limit"
        >
          <CreditLimitForm
            onSubmit={handleCreditUpdate}
            onCancel={() => setShowCreditModal(false)}
          />
        </Modal>
      )}

      {/* Settle Modal */}
      {showSettleModal && settleCustomer && (
        <Modal
          isOpen={showSettleModal}
          onClose={() => {
            setShowSettleModal(false);
            setSettleCustomer(null);
            setSelectedOrders([]);
          }}
          title={`Settle Credit - ${settleCustomer.name}`}
        >
          <SettleOrdersForm
            customer={settleCustomer}
            orders={settleOrders}
            selectedOrders={selectedOrders}
            onOrdersChange={setSelectedOrders}
            onSubmit={handleSettle}
            onCancel={() => setShowSettleModal(false)}
          />
        </Modal>
      )}

      {/* Refund Modal */}
      {showRefundModal && selectedPayment && (
        <Modal
          isOpen={showRefundModal}
          onClose={() => {
            setShowRefundModal(false);
            setRefundReason('');
          }}
          title="↩ Refund Payment"
        >
          <div className="ps-modal-form">
            <div style={{
              background: '#fff7ed',
              border: '1.5px solid #fed7aa',
              borderRadius: 10,
              padding: 16,
              marginBottom: 16
            }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <span style={{ fontSize: 24 }}>⚠️</span>
                <div>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>Confirm Refund</div>
                  <div style={{ fontSize: 13, color: '#92400e' }}>
                    You are about to refund <strong>{formatCurrency(selectedPayment.amount)}</strong> for transaction {selectedPayment.transactionId}. This action cannot be undone.
                  </div>
                </div>
              </div>
            </div>

            <div className="ps-modal-field">
              <label>Refund Reason *</label>
              <textarea
                className="ps-modal-input"
                placeholder="Enter reason for refund..."
                value={refundReason}
                onChange={(e) => setRefundReason(e.target.value)}
                rows={4}
                required
              />
            </div>

            <div className="ps-modal-actions">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowRefundModal(false);
                  setRefundReason('');
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleRefund}
                disabled={!refundReason.trim() || refundLoading}
                style={{ background: '#ef4444' }}
              >
                {refundLoading ? 'Processing...' : '↩ Refund Payment'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default PaymentSystem;

// Payment Table Component
const PaymentTable = ({ payments, onPaymentClick, onPrintReceipt, onGenerateInvoice }) => {
  if (payments.length === 0) {
    return (
      <div className="ps-table-wrap">
        <table className="ps-table">
          <thead>
            <tr>
              <th>Transaction ID</th>
              <th>Customer</th>
              <th>Order #</th>
              <th>Method</th>
              <th>Amount</th>
              <th>Received</th>
              <th>Change</th>
              <th>Status</th>
              <th>Date</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td colSpan="10" className="ps-empty">
                <div style={{ fontSize: 32, marginBottom: 6 }}>💳</div>
                No payments found
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="ps-table-wrap">
      <table className="ps-table">
        <thead>
          <tr>
            <th>Transaction ID</th>
            <th>Customer</th>
            <th>Order #</th>
            <th>Method</th>
            <th>Amount</th>
            <th>Received</th>
            <th>Change</th>
            <th>Status</th>
            <th>Date</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {payments.map(payment => {
            const sm = STATUS_META[payment.status] || STATUS_META.pending;
            const mm = METHOD_META[payment.method] || { label: payment.method, color: '#374151', icon: '💰' };

            return (
              <tr
                key={payment._id}
                className="ps-row"
                onClick={() => onPaymentClick(payment)}
              >
                <td>
                  <span className="ps-txn-id">{payment.transactionId}</span>
                </td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div className="om-avatar-sm">
                      {payment.customer?.name?.charAt(0).toUpperCase() || '?'}
                    </div>
                    <div>
                      <div className="om-cust-name-cell">{payment.customer?.name || 'Unknown'}</div>
                      <div className="om-cust-phone-cell">
                        {payment.customer?.phone ? formatPhone(payment.customer.phone) : '—'}
                      </div>
                    </div>
                  </div>
                </td>
                <td>
                  {payment.order?.orderNumber ? (
                    <span className="ps-order-num">{payment.order.orderNumber}</span>
                  ) : (
                    <span style={{ color: '#94a3b8' }}>—</span>
                  )}
                </td>
                <td>
                  <span style={{ fontSize: 12, fontWeight: 700, color: mm.color }}>
                    {mm.icon} {mm.label}
                  </span>
                </td>
                <td style={{ fontWeight: 800, color: '#0f172a' }}>
                  {formatCurrency(payment.amount)}
                </td>
                <td style={{ fontWeight: 600, color: '#374151' }}>
                  {formatCurrency(payment.receivedAmount || payment.amount)}
                </td>
                <td style={{ fontWeight: 600, color: payment.changeAmount > 0 ? '#10b981' : '#94a3b8' }}>
                  {payment.changeAmount > 0 ? formatCurrency(payment.changeAmount) : '—'}
                </td>
                <td>
                  <span className="om-badge" style={{ background: sm.bg, color: sm.color }}>
                    {sm.icon} {sm.label}
                  </span>
                </td>
                <td className="ps-date">
                  {formatDateTime(payment.createdAt)}
                </td>
                <td onClick={(e) => e.stopPropagation()}>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button
                      className="om-action-btn"
                      onClick={(e) => { e.stopPropagation(); onPrintReceipt(payment._id); }}
                      title="Print receipt"
                    >
                      🖨️
                    </button>
                    <button
                      className="om-action-btn"
                      onClick={(e) => { e.stopPropagation(); onGenerateInvoice(payment); }}
                      title="Generate invoice"
                    >
                      📄
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// Payment Detail Modal Content
const PaymentDetailContent = ({ payment, onPrintReceipt, onGenerateInvoice, onRefund }) => {
  const sm = STATUS_META[payment.status] || STATUS_META.pending;
  const mm = METHOD_META[payment.method] || { label: payment.method, color: '#374151', icon: '💰' };
  const canRefund = payment.status === 'paid';

  return (
    <div className="om-detail">
      {/* Top Bar */}
      <div className="om-detail-top-bar">
        <div className="om-detail-badges">
          <span className="om-badge" style={{ background: sm.bg, color: sm.color }}>
            {sm.icon} {sm.label}
          </span>
          <span className="om-badge" style={{ background: '#f1f5f9', color: mm.color }}>
            {mm.icon} {mm.label}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {canRefund && (
            <button className="om-icon-btn" onClick={onRefund} style={{ color: '#ef4444' }}>
              ↩ Refund
            </button>
          )}
          <button className="om-icon-btn" onClick={() => onPrintReceipt(payment._id)}>
            🖨️ Print Receipt
          </button>
          <button className="om-icon-btn" onClick={() => onGenerateInvoice(payment)}>
            📄 Invoice
          </button>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="om-detail-2col">
        {/* Left Column */}
        <div className="om-detail-section">
          <div className="om-section-title">Payment Information</div>
          <div className="om-info-row">
            <span className="om-info-label">Transaction ID</span>
            <span className="om-info-val" style={{ fontFamily: 'monospace', color: '#6366f1' }}>
              {payment.transactionId}
            </span>
          </div>
          <div className="om-info-row">
            <span className="om-info-label">Amount</span>
            <span className="om-info-val" style={{ fontSize: 16, fontWeight: 800 }}>
              {formatCurrency(payment.amount)}
            </span>
          </div>
          <div className="om-info-row">
            <span className="om-info-label">Received Amount</span>
            <span className="om-info-val">{formatCurrency(payment.receivedAmount || payment.amount)}</span>
          </div>
          {payment.changeAmount > 0 && (
            <div className="om-info-row">
              <span className="om-info-label">Change Given</span>
              <span className="om-info-val" style={{ color: '#10b981' }}>
                {formatCurrency(payment.changeAmount)}
              </span>
            </div>
          )}
          <div className="om-info-row">
            <span className="om-info-label">Payment Method</span>
            <span className="om-info-val">{mm.icon} {mm.label}</span>
          </div>
          {payment.reference && (
            <div className="om-info-row">
              <span className="om-info-label">Reference</span>
              <span className="om-info-val">{payment.reference}</span>
            </div>
          )}
          <div className="om-info-row">
            <span className="om-info-label">Date & Time</span>
            <span className="om-info-val">{formatDateTime(payment.createdAt)}</span>
          </div>
          <div className="om-info-row">
            <span className="om-info-label">Processed By</span>
            <span className="om-info-val">{payment.processedBy?.name || '—'}</span>
          </div>
        </div>

        {/* Right Column */}
        <div className="om-detail-section">
          <div className="om-section-title">Customer & Order</div>
          
          {/* Customer Card */}
          {payment.customer && (
            <div className="om-customer-card">
              <div className="om-avatar-lg">
                {payment.customer.name?.charAt(0).toUpperCase() || '?'}
              </div>
              <div className="om-customer-info">
                <div className="om-cust-name-lg">{payment.customer.name || 'Unknown'}</div>
                <div className="om-cust-detail">
                  📱 {payment.customer.phone ? formatPhone(payment.customer.phone) : '—'}
                </div>
                {payment.customer.email && (
                  <div className="om-cust-detail">✉️ {payment.customer.email}</div>
                )}
                
                {/* Financial Info */}
                {((payment.customer.walletBalance || 0) > 0 || (payment.customer.creditLimit || 0) > 0) && (
                  <div className="om-finance-chips">
                    {(payment.customer.walletBalance || 0) > 0 && (
                      <span className="om-finance-chip green">
                        👛 Wallet: {formatCurrency(payment.customer.walletBalance)}
                      </span>
                    )}
                    {(payment.customer.creditLimit || 0) > 0 && (
                      <span className="om-finance-chip amber">
                        📝 Credit: {formatCurrency(payment.customer.creditUsed || 0)}/{formatCurrency(payment.customer.creditLimit)}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Order Info */}
          {payment.order && (
            <div style={{ marginTop: 16 }}>
              <div className="om-section-title">Related Order</div>
              <div className="om-info-row">
                <span className="om-info-label">Order Number</span>
                <span className="om-info-val">{payment.order.orderNumber}</span>
              </div>
              {payment.order.totalAmount && (
                <div className="om-info-row">
                  <span className="om-info-label">Order Total</span>
                  <span className="om-info-val">{formatCurrency(payment.order.totalAmount)}</span>
                </div>
              )}
              {payment.order.status && (
                <div className="om-info-row">
                  <span className="om-info-label">Order Status</span>
                  <span className="om-info-val">{payment.order.status}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Notes */}
      {payment.notes && (
        <div className="om-notes-box">
          <strong style={{ fontSize: 12, color: '#92400e' }}>📝 Notes:</strong>
          <div style={{ marginTop: 6, fontSize: 13, color: '#374151' }}>{payment.notes}</div>
        </div>
      )}
    </div>
  );
};

// Wallet Tab Component
const WalletTab = ({ walletSummary, onLoad, onDeposit }) => {
  useEffect(() => {
    onLoad();
  }, []);

  if (!walletSummary) {
    return <div className="ps-loading">⏳ Loading wallet data...</div>;
  }

  return (
    <div>
      <div className="ps-wallet-summary">
        <div className="ps-wallet-sum-card credit">
          <span className="ps-wallet-sum-icon">💰</span>
          <div>
            <div className="ps-wallet-sum-value">{formatCurrency(walletSummary.totalBalance || 0)}</div>
            <div className="ps-wallet-sum-label">Total Balance</div>
          </div>
        </div>
        <div className="ps-wallet-sum-card credit">
          <span className="ps-wallet-sum-icon">👥</span>
          <div>
            <div className="ps-wallet-sum-value">{walletSummary.activeWallets || 0}</div>
            <div className="ps-wallet-sum-label">Active Wallets</div>
          </div>
        </div>
        <div className="ps-wallet-sum-card debit">
          <span className="ps-wallet-sum-icon">📊</span>
          <div>
            <div className="ps-wallet-sum-value">{formatCurrency(walletSummary.avgBalance || 0)}</div>
            <div className="ps-wallet-sum-label">Avg Balance</div>
          </div>
        </div>
      </div>

      <div className="ps-outstanding-header">
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Customer Wallets</h3>
        <Button onClick={onDeposit}>💵 Deposit to Wallet</Button>
      </div>

      <div className="ps-outstanding-grid">
        {walletSummary.customers && walletSummary.customers.length > 0 ? (
          walletSummary.customers.map(customer => (
            <div key={customer._id} className="ps-credit-card">
              <div className="ps-credit-card-header">
                <div className="ps-credit-avatar">
                  {customer.name?.charAt(0).toUpperCase() || '?'}
                </div>
                <div className="ps-credit-info">
                  <div className="ps-credit-name">{customer.name || 'Unknown'}</div>
                  <div className="ps-credit-phone">{formatPhone(customer.phone)}</div>
                </div>
              </div>
              <div style={{ textAlign: 'center', padding: '12px 0' }}>
                <div style={{ fontSize: 24, fontWeight: 800, color: '#10b981' }}>
                  {formatCurrency(customer.walletBalance || 0)}
                </div>
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>CURRENT BALANCE</div>
              </div>
            </div>
          ))
        ) : (
          <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: 40, color: '#94a3b8' }}>
            No active wallets
          </div>
        )}
      </div>
    </div>
  );
};

// Credit Tab Component
const CreditTab = ({ creditSummary, outstandingCredits, onLoad, onSettle, onManageCredit }) => {
  useEffect(() => {
    onLoad();
  }, []);

  if (!creditSummary) {
    return <div className="ps-loading">⏳ Loading credit data...</div>;
  }

  return (
    <div>
      <div className="ps-wallet-summary">
        <div className="ps-wallet-sum-card debit">
          <span className="ps-wallet-sum-icon">💳</span>
          <div>
            <div className="ps-wallet-sum-value">{formatCurrency(creditSummary.totalOutstanding || 0)}</div>
            <div className="ps-wallet-sum-label">Outstanding Credit</div>
          </div>
        </div>
        <div className="ps-wallet-sum-card credit">
          <span className="ps-wallet-sum-icon">📊</span>
          <div>
            <div className="ps-wallet-sum-value">{formatCurrency(creditSummary.totalLimit || 0)}</div>
            <div className="ps-wallet-sum-label">Total Credit Limit</div>
          </div>
        </div>
        <div className="ps-wallet-sum-card">
          <span className="ps-wallet-sum-icon">👥</span>
          <div>
            <div className="ps-wallet-sum-value">{creditSummary.activeCredits || 0}</div>
            <div className="ps-wallet-sum-label">Active Credits</div>
          </div>
        </div>
      </div>

      <div className="ps-outstanding-header">
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Outstanding Credits</h3>
        <div className="ps-outstanding-stats">
          <span>{outstandingCredits?.length || 0} customers</span>
        </div>
        <Button onClick={onManageCredit}>📝 Manage Credit</Button>
      </div>

      <div className="ps-outstanding-grid">
        {outstandingCredits && outstandingCredits.length > 0 ? (
          outstandingCredits.map(customer => {
            const usedPct = ((customer.creditUsed || 0) / (customer.creditLimit || 1)) * 100;
            const availableCredit = (customer.creditLimit || 0) - (customer.creditUsed || 0);
            const barColor = usedPct > 90 ? '#ef4444' : usedPct > 70 ? '#f59e0b' : '#10b981';

            return (
              <div key={customer._id} className="ps-credit-card">
                <div className="ps-credit-card-header">
                  <div className="ps-credit-avatar">
                    {customer.name?.charAt(0).toUpperCase() || '?'}
                  </div>
                  <div className="ps-credit-info">
                    <div className="ps-credit-name">{customer.name || 'Unknown'}</div>
                    <div className="ps-credit-phone">{formatPhone(customer.phone)}</div>
                  </div>
                </div>
                <div className="ps-credit-amounts">
                  <div className="ps-credit-used">
                    <span>Used</span>
                    <strong style={{ color: '#ef4444' }}>{formatCurrency(customer.creditUsed || 0)}</strong>
                  </div>
                  <div className="ps-credit-avail">
                    <span>Available</span>
                    <strong style={{ color: '#10b981' }}>{formatCurrency(availableCredit)}</strong>
                  </div>
                  <div className="ps-credit-limit">
                    <span>Limit</span>
                    <strong>{formatCurrency(customer.creditLimit || 0)}</strong>
                  </div>
                </div>
                <div className="ps-credit-bar-wrap">
                  <div className="ps-credit-bar-fill" style={{ width: `${usedPct}%`, background: barColor }} />
                </div>
                <div className="ps-credit-pct">{usedPct.toFixed(1)}% used</div>
                {(customer.creditUsed || 0) > 0 && (
                  <button
                    className="ps-settle-btn-lg"
                    onClick={() => onSettle(customer)}
                    style={{ marginTop: 8, width: '100%' }}
                  >
                    💰 Settle Credit
                  </button>
                )}
              </div>
            );
          })
        ) : (
          <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: 40, color: '#94a3b8' }}>
            No outstanding credits
          </div>
        )}
      </div>
    </div>
  );
};

// Wallet Deposit Form
const WalletDepositForm = ({ onSubmit, onCancel }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('cash');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (searchTerm.length >= 2) {
      searchCustomers(searchTerm);
    } else {
      setCustomers([]);
    }
  }, [searchTerm]);

  const searchCustomers = async (query) => {
    try {
      const data = await customersAPI.search({ query, limit: 10 });
      setCustomers(data.data || []);
    } catch (error) {
      console.error('Error searching customers:', error);
    }
  };

  const handleSubmit = () => {
    if (!selectedCustomer || !amount || parseFloat(amount) <= 0) {
      alert('Please select a customer and enter a valid amount');
      return;
    }
    onSubmit({
      customerId: selectedCustomer._id,
      amount: parseFloat(amount),
      method,
      reference,
      notes,
    });
  };

  return (
    <div className="ps-modal-form">
      {!selectedCustomer ? (
        <div className="ps-modal-field">
          <label>Search Customer</label>
          <input
            type="text"
            className="ps-modal-input"
            placeholder="Enter customer name or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            autoFocus
          />
          {customers.length > 0 && (
            <div className="ps-customer-dropdown">
              {customers.map(customer => (
                <div
                  key={customer._id}
                  className="ps-customer-option"
                  onClick={() => {
                    setSelectedCustomer(customer);
                    setSearchTerm('');
                  }}
                >
                  <strong>{customer.name}</strong>
                  <span>{formatPhone(customer.phone)}</span>
                  <span className="ps-wallet-chip">
                    {formatCurrency(customer.walletBalance || 0)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="ps-selected-customer">
            <div className="ps-sel-info">
              <strong>{selectedCustomer.name}</strong>
              <span>📱 {formatPhone(selectedCustomer.phone)}</span>
              <span className="ps-wallet-chip">
                Current: {formatCurrency(selectedCustomer.walletBalance || 0)}
              </span>
            </div>
            <button className="ps-clear-customer" onClick={() => setSelectedCustomer(null)}>✕</button>
          </div>

          <div className="ps-modal-field">
            <label>Deposit Amount</label>
            <input
              type="number"
              className="ps-modal-input ps-amount-input"
              placeholder="0.000"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              min="0"
              step="0.001"
              autoFocus
            />
          </div>

          <div className="ps-modal-field">
            <label>Payment Method</label>
            <div className="ps-method-buttons">
              {['cash', 'card', 'bank_transfer'].map(m => (
                <button
                  key={m}
                  className={`ps-method-btn ${method === m ? 'active' : ''}`}
                  onClick={() => setMethod(m)}
                  type="button"
                >
                  {m === 'cash' ? '💵 Cash' : m === 'card' ? '💳 Card' : '🏦 Bank'}
                </button>
              ))}
            </div>
          </div>

          {method !== 'cash' && (
            <div className="ps-modal-field">
              <label>Reference / Transaction ID</label>
              <input
                type="text"
                className="ps-modal-input"
                placeholder="Enter reference number..."
                value={reference}
                onChange={(e) => setReference(e.target.value)}
              />
            </div>
          )}

          <div className="ps-modal-field">
            <label>Notes (Optional)</label>
            <textarea
              className="ps-modal-input"
              placeholder="Add any notes..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>

          <div className="ps-modal-actions">
            <Button variant="secondary" onClick={onCancel}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={!amount || parseFloat(amount) <= 0}>
              💰 Deposit {amount ? formatCurrency(parseFloat(amount)) : ''}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

// Credit Limit Form
const CreditLimitForm = ({ onSubmit, onCancel }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [creditLimit, setCreditLimit] = useState('');

  useEffect(() => {
    if (selectedCustomer) {
      setCreditLimit((selectedCustomer.creditLimit || 0).toString());
    }
  }, [selectedCustomer]);

  useEffect(() => {
    if (searchTerm.length >= 2) {
      searchCustomers(searchTerm);
    } else {
      setCustomers([]);
    }
  }, [searchTerm]);

  const searchCustomers = async (query) => {
    try {
      const data = await customersAPI.search({ query, limit: 10 });
      setCustomers(data.data || []);
    } catch (error) {
      console.error('Error searching customers:', error);
    }
  };

  const handleSubmit = () => {
    if (!selectedCustomer || creditLimit === '') {
      alert('Please select a customer and enter a credit limit');
      return;
    }
    onSubmit({
      customerId: selectedCustomer._id,
      creditLimit: parseFloat(creditLimit),
    });
  };

  return (
    <div className="ps-modal-form">
      {!selectedCustomer ? (
        <div className="ps-modal-field">
          <label>Search Customer</label>
          <input
            type="text"
            className="ps-modal-input"
            placeholder="Enter customer name or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            autoFocus
          />
          {customers.length > 0 && (
            <div className="ps-customer-dropdown">
              {customers.map(customer => (
                <div
                  key={customer._id}
                  className="ps-customer-option"
                  onClick={() => {
                    setSelectedCustomer(customer);
                    setSearchTerm('');
                  }}
                >
                  <strong>{customer.name}</strong>
                  <span>{formatPhone(customer.phone)}</span>
                  {(customer.creditLimit || 0) > 0 && (
                    <span className="ps-wallet-chip" style={{ background: '#fef3c7', color: '#92400e' }}>
                      Limit: {formatCurrency(customer.creditLimit)}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="ps-selected-customer">
            <div className="ps-sel-info">
              <strong>{selectedCustomer.name}</strong>
              <span>📱 {formatPhone(selectedCustomer.phone)}</span>
              {(selectedCustomer.creditLimit || 0) > 0 && (
                <span className="ps-wallet-chip" style={{ background: '#fef3c7', color: '#92400e' }}>
                  Current Limit: {formatCurrency(selectedCustomer.creditLimit)}
                </span>
              )}
            </div>
            <button className="ps-clear-customer" onClick={() => setSelectedCustomer(null)}>✕</button>
          </div>

          <div className="ps-modal-field">
            <label>Credit Limit</label>
            <input
              type="number"
              className="ps-modal-input ps-amount-input"
              placeholder="0.000"
              value={creditLimit}
              onChange={(e) => setCreditLimit(e.target.value)}
              min="0"
              step="0.001"
              autoFocus
            />
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 6 }}>
              Set to 0 to disable credit for this customer
            </div>
          </div>

          {(selectedCustomer.creditUsed || 0) > 0 && (
            <div style={{
              background: '#fffbeb',
              border: '1px solid #fde68a',
              borderRadius: 8,
              padding: 12,
              fontSize: 13,
              color: '#92400e'
            }}>
              ⚠️ Customer has {formatCurrency(selectedCustomer.creditUsed)} in outstanding credit
            </div>
          )}

          <div className="ps-modal-actions">
            <Button variant="secondary" onClick={onCancel}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={creditLimit === ''}>
              💳 Update Limit
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

// Settle Orders Form
const SettleOrdersForm = ({ customer, orders, selectedOrders, onOrdersChange, onSubmit, onCancel }) => {
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [useWallet, setUseWallet] = useState(false);

  const totalAmount = orders
    .filter(order => selectedOrders.includes(order._id))
    .reduce((sum, order) => sum + (order.totalAmount || 0), 0);

  const handleOrderToggle = (orderId) => {
    if (selectedOrders.includes(orderId)) {
      onOrdersChange(selectedOrders.filter(id => id !== orderId));
    } else {
      onOrdersChange([...selectedOrders, orderId]);
    }
  };

  const handleSubmit = () => {
    if (selectedOrders.length === 0) {
      alert('Please select at least one order to settle');
      return;
    }
    onSubmit({ paymentMethod, useWallet });
  };

  return (
    <div className="ps-modal-form">
      <div className="ps-settle-customer-banner">
        <div className="ps-credit-avatar">
          {customer.name?.charAt(0).toUpperCase() || '?'}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, color: '#0f172a' }}>{customer.name}</div>
          <div style={{ fontSize: 12, color: '#64748b' }}>
            📱 {formatPhone(customer.phone)}
          </div>
        </div>
        {(customer.walletBalance || 0) > 0 && (
          <div style={{
            background: '#d1fae5',
            color: '#065f46',
            padding: '4px 12px',
            borderRadius: 20,
            fontSize: 12,
            fontWeight: 700
          }}>
            👛 {formatCurrency(customer.walletBalance)}
          </div>
        )}
      </div>

      <div className="ps-modal-field">
        <label>Select Orders to Settle</label>
        {orders.length > 0 ? (
          <div className="ps-settle-orders-list">
            {orders.map(order => (
              <div key={order._id} className="ps-settle-order-row">
                <input
                  type="checkbox"
                  checked={selectedOrders.includes(order._id)}
                  onChange={() => handleOrderToggle(order._id)}
                />
                <span className="ps-order-num">{order.orderNumber}</span>
                <span className="ps-order-date">
                  {new Date(order.createdAt).toLocaleDateString()}
                </span>
                <strong>{formatCurrency(order.totalAmount || 0)}</strong>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: 20, color: '#94a3b8' }}>
            No orders found with outstanding credit
          </div>
        )}
      </div>

      {selectedOrders.length > 0 && (
        <>
          <div style={{
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: 10,
            padding: '14px 16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span style={{ fontSize: 14, color: '#64748b' }}>Total to Settle</span>
            <span style={{ fontSize: 20, fontWeight: 800, color: '#0f172a' }}>
              {formatCurrency(totalAmount)}
            </span>
          </div>

          {(customer.walletBalance || 0) > 0 && (
            <div className="ps-checkbox-label">
              <input
                type="checkbox"
                checked={useWallet}
                onChange={(e) => setUseWallet(e.target.checked)}
              />
              <span>Use wallet balance ({formatCurrency(customer.walletBalance)})</span>
            </div>
          )}

          <div className="ps-modal-field">
            <label>Payment Method</label>
            <div className="ps-method-buttons">
              {['cash', 'card', 'bank_transfer'].map(m => (
                <button
                  key={m}
                  className={`ps-method-btn ${paymentMethod === m ? 'active' : ''}`}
                  onClick={() => setPaymentMethod(m)}
                  type="button"
                >
                  {m === 'cash' ? '💵 Cash' : m === 'card' ? '💳 Card' : '🏦 Bank'}
                </button>
              ))}
            </div>
          </div>

          <div className="ps-modal-actions">
            <Button variant="secondary" onClick={onCancel}>Cancel</Button>
            <Button onClick={handleSubmit}>
              💰 Settle {formatCurrency(totalAmount)}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
